// components/MapaLocalizacion.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Solucionar problema de iconos predeterminados en Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Precargar recursos críticos
const preloadResources = () => {
  if (typeof window === 'undefined') return;
  
  const preloadImage = (url) => {
    const img = new Image();
    img.src = url;
  };
  
  preloadImage('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png');
  preloadImage('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png');
  preloadImage('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png');
};

if (typeof window !== 'undefined') {
  setTimeout(preloadResources, 0);
}

const MapaLocalizacion = ({ 
  latitud, 
  longitud, 
  onLocationChange, 
  editable = true,
  direccion = ''
}) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [ubicacionInfo, setUbicacionInfo] = useState({
    estado: '',
    municipio: '',
    ciudad: '',
    direccion: direccion,
    latitud: latitud || '',
    longitud: longitud || ''
  });

  // Función para forzar el redimensionamiento del mapa
  const forceMapResize = useCallback(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize(true);
        mapRef.current._onResize(); // Forzar actualización visual
      }, 100);
    }
  }, []);

  // Observar cambios en el contenedor del mapa
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) {
        setTimeout(() => {
          mapRef.current.invalidateSize(true);
        }, 100);
      }
    });

    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapInitialized]);

  // Función para obtener información de la ubicación mediante reverse geocoding
  const obtenerInfoUbicacion = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await response.json();
      
      if (data && data.address) {
        const { address } = data;
        setUbicacionInfo(prev => ({
          ...prev,
          estado: address.state || address.region || '',
          municipio: address.county || address.municipality || '',
          ciudad: address.city || address.town || address.village || '',
          direccion: data.display_name || direccion || '',
          latitud: lat,
          longitud: lng
        }));
      }
    } catch (error) {
      console.error('Error al obtener información de la ubicación:', error);
    }
  }, [direccion]);

  // Función para actualizar el marcador en el mapa
  const actualizarMarcador = useCallback((lat, lng) => {
    if (!mapRef.current) return;
    
    // Eliminar marcador existente si hay uno
    if (markerRef.current) {
      mapRef.current.removeLayer(markerRef.current);
    }
    
    // Crear nuevo marcador
    markerRef.current = L.marker([lat, lng]).addTo(mapRef.current);
    
    // Centrar el mapa en la nueva ubicación
    mapRef.current.setView([lat, lng], 13);
    
    // Obtener información de la ubicación
    obtenerInfoUbicacion(lat, lng);
  }, [obtenerInfoUbicacion]);

  // Inicializar el mapa
  const inicializarMapa = useCallback(() => {
    if (!mapContainerRef.current || mapInitialized) return;
    
    try {
      setMapLoading(true);
      setMapError(null);
      
      // Valores por defecto si no se proporcionan coordenadas
      const defaultLat = latitud || 10.5000;
      const defaultLng = longitud || -66.9167;
      
      // Crear el mapa con opciones optimizadas
      mapRef.current = L.map(mapContainerRef.current, {
        zoomControl: false,
        loadingControl: false,
        fadeAnimation: true,
        zoomAnimation: true,
        preferCanvas: true,
      }).setView([defaultLat, defaultLng], 13);
      
      // Añadir controles de zoom con mejor posición
      L.control.zoom({
        position: 'topright'
      }).addTo(mapRef.current);
      
      // Añadir capa de OpenStreetMap
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        minZoom: 2,
        crossOrigin: true
      }).addTo(mapRef.current);

      // Añadir marcador inicial si hay coordenadas
      if (latitud && longitud) {
        actualizarMarcador(latitud, longitud);
      }

      // Permitir añadir/editar marcador si es editable
      if (editable) {
        mapRef.current.on('click', (e) => {
          const { lat, lng } = e.latlng;
          actualizarMarcador(lat, lng);
          if (onLocationChange) onLocationChange(lat, lng);
        });
      }

      setMapInitialized(true);
      setMapLoading(false);
      
      // Forzar redimensionamiento después de la inicialización
      setTimeout(() => {
        forceMapResize();
      }, 300);
    } catch (error) {
      console.error('Error al inicializar el mapa:', error);
      setMapError('Error al cargar el mapa. Por favor, recarga la página.');
      setMapLoading(false);
    }
  }, [mapInitialized, latitud, longitud, editable, onLocationChange, actualizarMarcador, forceMapResize]);

  useEffect(() => {
    // Inicializar el mapa cuando el componente se monta
    if (!mapInitialized) {
      const timer = setTimeout(inicializarMapa, 100);
      return () => clearTimeout(timer);
    }
  }, [mapInitialized, inicializarMapa]);

  // Efecto para actualizar el marcador cuando cambian las coordenadas
  useEffect(() => {
    if (mapInitialized && latitud && longitud) {
      actualizarMarcador(latitud, longitud);
    }
  }, [mapInitialized, latitud, longitud, actualizarMarcador]);

  // Efecto para actualizar información cuando cambia la dirección desde props
  useEffect(() => {
    if (direccion && direccion !== ubicacionInfo.direccion) {
      setUbicacionInfo(prev => ({
        ...prev,
        direccion: direccion
      }));
    }
  }, [direccion, ubicacionInfo.direccion]);

  return (
    <div>
      {mapLoading && (
        <div className="mapa-cargando" style={{
          height: '400px', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          marginBottom: '15px'
        }}>
          <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Cargando mapa...</span>
          </div>
          <p className="mt-3">Cargando mapa...</p>
        </div>
      )}
      
      {mapError && (
        <div style={{
          height: '400px', 
          width: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          marginBottom: '15px',
          color: '#dc3545',
          fontWeight: 'bold'
        }}>
          {mapError}
        </div>
      )}
      
      <div 
        ref={mapContainerRef} 
        style={{ 
          height: '400px', 
          width: '100%', 
          borderRadius: '5px', 
          marginBottom: '15px',
          display: mapLoading || mapError ? 'none' : 'block'
        }}
        className="map-container"
      />
      
      {/* Información de la ubicación */}
      <div className="ubicacion-info p-3 border rounded">
        <h6 className="mb-3">Información de la Ubicación</h6>
        
        <div className="row">
          <div className="col-md-6 mb-2">
            <strong>Estado:</strong>
            <div className="text-muted">{ubicacionInfo.estado || 'No especificado'}</div>
          </div>
          
          <div className="col-md-6 mb-2">
            <strong>Municipio:</strong>
            <div className="text-muted">{ubicacionInfo.municipio || 'No especificado'}</div>
          </div>
          
          <div className="col-md-6 mb-2">
            <strong>Ciudad:</strong>
            <div className="text-muted">{ubicacionInfo.ciudad || 'No especificado'}</div>
          </div>
          
          <div className="col-12 mb-2">
            <strong>Dirección:</strong>
            <div className="text-muted">{ubicacionInfo.direccion || 'No especificada'}</div>
          </div>
          
          <div className="col-md-3 mb-2">
            <strong>Latitud:</strong>
            <div className="text-muted">{ubicacionInfo.latitud || 'No especificada'}</div>
          </div>
          
          <div className="col-md-3 mb-2">
            <strong>Longitud:</strong>
            <div className="text-muted">{ubicacionInfo.longitud || 'No especificada'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapaLocalizacion;